import { useMutation, useQueryClient } from "@tanstack/react-query";
import { httpClient } from "@/shared/api/http-client";
import { assertIdSegment } from "@/shared/lib/id-segment";
import { USER_DETAIL_QUERY_KEY, USER_MEMBERSHIPS_QUERY_KEY } from "../constants";
import type { AdminUpdateStaffAssignmentsInput, AdminUpdateStaffAssignmentsResult } from "./types";

/** Organization reads (members, detail, locations) also show the staff assignments. */
const ORGANIZATIONS_QUERY_KEY = ["admin", "organizations"] as const;

const JSON_HEADERS = { "content-type": "application/json" } as const;

function staffPath(userId: string): string {
  return `/api/admin/users/${assertIdSegment(userId, "user id")}/staff`;
}

function useInvalidateStaff() {
  const queryClient = useQueryClient();
  return (userId: string) =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: [...USER_DETAIL_QUERY_KEY, userId] }),
      queryClient.invalidateQueries({ queryKey: [...USER_MEMBERSHIPS_QUERY_KEY, userId] }),
      queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEY }),
    ]);
}

function updatePathAndMethod(
  userId: string,
  action: AdminUpdateStaffAssignmentsInput["action"],
): { readonly path: string; readonly method: "POST" | "DELETE" } {
  const base = `${staffPath(userId)}/assignments`;
  if (action === "remove") return { path: base, method: "DELETE" };
  return { path: `${base}/${action}`, method: "POST" };
}

/**
 * Bulk deactivate / reactivate / remove of the user's assignments.
 * `remove` is `DELETE …/assignments` with the ids in the body. ADMIN only.
 */
export function useAdminUpdateStaffAssignments() {
  const invalidate = useInvalidateStaff();
  return useMutation({
    mutationFn: ({ userId, action, ...body }: AdminUpdateStaffAssignmentsInput) => {
      const { path, method } = updatePathAndMethod(userId, action);
      return httpClient<AdminUpdateStaffAssignmentsResult>(path, {
        method,
        headers: JSON_HEADERS,
        body: JSON.stringify(body),
      });
    },
    onSuccess: (_result, variables) => invalidate(variables.userId),
  });
}
